import { useEffect, useRef, useState, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useAuth } from '../../contexts/AuthContext'
import { getServerLLMBaseUrl } from '../../config/api'
import './ClinicalAIAssistant.css'
import duLogo1 from '../../assets/serverllm/du-logo-1.png'
import duLogo2 from '../../assets/serverllm/du-logo-2.png'
import duLogo3 from '../../assets/serverllm/du-logo-3.png'
import duLogo4 from '../../assets/serverllm/du-logo-4.png'
import duLogo5 from '../../assets/serverllm/du-logo-5.png'
import duLogo6 from '../../assets/serverllm/du-logo-6.png'

// API_URL is computed at call time so it picks up the selected tenant slug from localStorage
const getApiUrl = () => getServerLLMBaseUrl()

interface Message {
  role: 'user' | 'assistant'
  content: string
  sources?: string[]
  createdAt?: string
  model?: string
  embedding?: number[]
  condensed?: string
  images?: string[]
  docs?: Array<{ name: string; dataUrl?: string; serverId?: string }>
}

interface Attachment {
  id: string
  type: 'document' | 'image'
  name: string
  dataUrl?: string
  serverId?: string
  file: File
  status: 'pending' | 'uploading' | 'ingesting' | 'done' | 'error'
  chunks?: number
  pages?: number
  error?: string
  progress?: number
}

interface DocSidebarEntry {
  id: string
  name: string
  status: 'uploading' | 'ingesting' | 'done' | 'error'
  chunks?: number
  pages?: number
  error?: string
  addedAt: string
  sessionId: string
  progress?: number
  documentId?: string
}

interface ModelOption {
  name: string
  provider: string
}

interface ChatSession {
  id: string
  title: string
  createdAt: string
  messages: Message[]
  starred?: boolean
  isCustomTitle?: boolean
  projectId?: string
  promptPresetId?: string
}

interface ChatResponsePayload {
  answer?: string
  sources?: string[]
  error?: string
  model?: string
  provider?: string
}

interface ModelsResponsePayload {
  models?: Array<{ name: string; provider?: string }>
  providers?: Record<string, { models: string[] }>
  default_provider?: string
  default_model?: string
}

interface AppSettings {
  defaultProvider: string
  defaultModel: string
  systemPrompt: string
  enableBackgrounds: boolean
}

interface Project {
  id: string
  name: string
}

interface PromptPreset {
  id: string
  name: string
  creatorName: string
  prompt: string
}

interface EmbedResponsePayload {
  embeddings?: number[][]
  count?: number
  error?: string
}

type QueryScope = 'all' | 'training'
type MainSection = 'chat' | 'settings'
type SettingsTab = 'defaults' | 'system-prompt' | 'ollama-models'

const SESSION_STORAGE_KEY = 'serverllm_sessions_v1'
const PROJECT_STORAGE_KEY = 'serverllm_projects_v1'
const GENERAL_PROJECT_ID = 'project_general'
const SETTINGS_STORAGE_KEY = 'serverllm_settings_v1'
const PROMPT_PRESETS_STORAGE_KEY = 'serverllm_prompt_presets_v1'
const ACTIVE_PROMPT_PRESET_STORAGE_KEY = 'serverllm_active_prompt_preset_v1'
const PROVIDER_STORAGE_KEY = 'serverllm_provider_v1'
const MODEL_STORAGE_KEY = 'serverllm_model_v1'
const SCOPE_STORAGE_KEY = 'serverllm_scope_v1'
const DOC_SIDEBAR_STORAGE_KEY = 'serverllm_doc_sidebar_v1'
const REQUEST_TIMEOUT_MS = 120000
const PROMPTS_PAGE_SIZE = 8
const DU_LOGOS = [duLogo1, duLogo2, duLogo3, duLogo4, duLogo5, duLogo6]

const pickRandomLogo = (exclude?: string): string => {
  const source = exclude ? DU_LOGOS.filter((logo) => logo !== exclude) : DU_LOGOS
  const pool = source.length > 0 ? source : DU_LOGOS
  return pool[Math.floor(Math.random() * pool.length)]
}

const detectProvider = (modelName: string): string => {
  const lowered = modelName.toLowerCase()
  if (lowered.startsWith('openai/') || lowered.includes('gpt-')) return 'openai'
  if (lowered.startsWith('anthropic/') || lowered.includes('claude')) return 'anthropic'
  if (lowered.startsWith('nvidia/') || lowered.includes('nemotron')) return 'nvidia'
  if (lowered.startsWith('lmstudio/')) return 'lmstudio'
  if (lowered.startsWith('huggingface/')) return 'huggingface'
  return 'ollama'
}

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const cosineSimilarity = (a: number[], b: number[]): number => {
  if (!a.length || !b.length || a.length !== b.length) return -1
  let dot = 0
  let normA = 0
  let normB = 0
  for (let i = 0; i < a.length; i += 1) {
    dot += a[i] * b[i]
    normA += a[i] * a[i]
    normB += b[i] * b[i]
  }
  if (normA === 0 || normB === 0) return -1
  return dot / Math.sqrt(normA * normB)
}

const formatMessageTimestamp = (value?: string): string => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleString([], {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function ClinicalAIAssistant() {
  const { token, user } = useAuth()
  const isGlobalAdmin = Boolean(user?.is_superuser)
  const authHeaders = useMemo(() => token ? { Authorization: `Token ${token}` } : {}, [token])

  const [promptPresets, setPromptPresets] = useState<PromptPreset[]>(() => {
    const raw = localStorage.getItem(PROMPT_PRESETS_STORAGE_KEY)
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as PromptPreset[]
        if (parsed.length > 0) {
          return parsed
        }
      } catch {
        localStorage.removeItem(PROMPT_PRESETS_STORAGE_KEY)
      }
    }
    return [
      {
        id: 'prompt_default',
        name: 'General RAG Analyst',
        creatorName: 'System',
        prompt: '',
      },
      {
        id: 'prompt_clinical',
        name: 'Clinical Document Review',
        creatorName: 'System',
        prompt: 'Focus on clinical accuracy, evidence-based recommendations, and patient safety considerations.',
      },
      {
        id: 'prompt_research',
        name: 'Research Summarisation',
        creatorName: 'System',
        prompt: 'Summarise research findings, highlight methodology, key outcomes, and limitations.',
      },
    ]
  })
  const [selectedPromptPresetId, setSelectedPromptPresetId] = useState<string>(() => {
    return localStorage.getItem(ACTIVE_PROMPT_PRESET_STORAGE_KEY) || 'prompt_default'
  })
  const [newPromptName, setNewPromptName] = useState('')
  const [editingPresetName, setEditingPresetName] = useState('')
  const [projects, setProjects] = useState<Project[]>(() => {
    const raw = localStorage.getItem(PROJECT_STORAGE_KEY)
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as Project[]
        if (parsed.length > 0) {
          return parsed
        }
      } catch {
        localStorage.removeItem(PROJECT_STORAGE_KEY)
      }
    }
    return [{ id: GENERAL_PROJECT_ID, name: 'General' }]
  })
  const [sessions, setSessions] = useState<ChatSession[]>([])
  const [activeSessionId, setActiveSessionId] = useState<string>('')
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [includeWeb, setIncludeWeb] = useState(false)
  const [model, setModel] = useState(() => localStorage.getItem(MODEL_STORAGE_KEY) || 'llama3.2:1b')
  const [availableModels, setAvailableModels] = useState<ModelOption[]>([])
  const [provider, setProvider] = useState(() => localStorage.getItem(PROVIDER_STORAGE_KEY) || 'ollama')
  const [queryScope, setQueryScope] = useState<QueryScope>(() => {
    const s = localStorage.getItem(SCOPE_STORAGE_KEY)
    return (s === 'all' || s === 'training') ? s : 'all'
  })
  const [settings, setSettings] = useState<AppSettings>(() => {
    const raw = localStorage.getItem(SETTINGS_STORAGE_KEY)
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as AppSettings
        return {
          defaultProvider: parsed.defaultProvider || 'ollama',
          defaultModel: parsed.defaultModel || 'llama3.2:1b',
          systemPrompt: parsed.systemPrompt || '',
          enableBackgrounds: parsed.enableBackgrounds !== false,
        }
      } catch {
        localStorage.removeItem(SETTINGS_STORAGE_KEY)
      }
    }
    return {
      defaultProvider: 'ollama',
      defaultModel: 'llama3.2:1b',
      systemPrompt: '',
      enableBackgrounds: true,
    }
  })
  const [activeSidebarLogo] = useState(() => pickRandomLogo())
  const [activeChatBackgroundLogo] = useState(() => pickRandomLogo(activeSidebarLogo))
  const [statusText, setStatusText] = useState('Ready')
  const [debugText, setDebugText] = useState('Idle')
  const [settingsStatus, setSettingsStatus] = useState('')
  const [mainSection, setMainSection] = useState<MainSection>('chat')
  const [settingsTab, setSettingsTab] = useState<SettingsTab>('defaults')
  const [promptPage, setPromptPage] = useState(1)
  const [newProjectName, setNewProjectName] = useState('')
  const [draggedSessionId, setDraggedSessionId] = useState<string | null>(null)
  const [pendingAttachments, setPendingAttachments] = useState<Attachment[]>([])
  const pendingAttachmentsRef = useRef<Attachment[]>([])
  useEffect(() => { pendingAttachmentsRef.current = pendingAttachments }, [pendingAttachments])
  const [isDragOver, setIsDragOver] = useState(false)
  const [docSidebar, setDocSidebar] = useState<DocSidebarEntry[]>(() => {
    try {
      const raw = localStorage.getItem(DOC_SIDEBAR_STORAGE_KEY)
      if (raw) {
        const parsed = JSON.parse(raw) as DocSidebarEntry[]
        return parsed.map((e) =>
          e.status === 'uploading' || e.status === 'ingesting'
            ? { ...e, status: 'done' }
            : e
        )
      }
    } catch { /* ignore */ }
    return []
  })
  const [docSidebarOpen, setDocSidebarOpen] = useState(false)
  const [ollamaPullModel, setOllamaPullModel] = useState('')
  const [ollamaPullStatus, setOllamaPullStatus] = useState('')
  const [isEditingTitle, setIsEditingTitle] = useState(false)
  const [lightbox, setLightbox] = useState<{ dataUrl: string; name: string; type: 'image' | 'doc' } | null>(null)

  useEffect(() => {
    if (!lightbox) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setLightbox(null) }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [lightbox])
  const [editingTitleValue, setEditingTitleValue] = useState('')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const requestCounterRef = useRef(0)

  const activeSession = sessions.find((session) => session.id === activeSessionId)
  const activeProjectId = activeSession?.projectId || projects[0]?.id || GENERAL_PROJECT_ID
  const activeConversationPromptPresetId = activeSession?.promptPresetId || 'prompt_default'
  const messages = activeSession?.messages ?? []
  const promptPageCount = Math.max(1, Math.ceil(promptPresets.length / PROMPTS_PAGE_SIZE))
  const pagedPromptPresets = promptPresets.slice((promptPage - 1) * PROMPTS_PAGE_SIZE, promptPage * PROMPTS_PAGE_SIZE)

  const updateActiveMessages = (updater: (prev: Message[]) => Message[]) => {
    setSessions((prevSessions) =>
      prevSessions.map((session) => {
        if (session.id !== activeSessionId) {
          return session
        }
        const nextMessages = updater(session.messages)
        const firstUserMessage = nextMessages.find((msg) => msg.role === 'user')
        const autoTitle = firstUserMessage ? firstUserMessage.content.slice(0, 50) : session.title
        return {
          ...session,
          title: session.isCustomTitle ? session.title : autoTitle,
          messages: nextMessages,
        }
      })
    )
  }

  const patchActiveMessageAtIndex = (index: number, patch: Partial<Message>) => {
    updateActiveMessages((prev) => {
      const next = [...prev]
      if (next[index]) {
        next[index] = { ...next[index], ...patch }
      }
      return next
    })
  }

  const embedTexts = async (texts: string[]): Promise<number[][]> => {
    if (!texts.length) return []
    const response = await fetch(`${getApiUrl()}/embed/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...authHeaders },
      body: JSON.stringify({ texts }),
    })
    if (!response.ok) {
      throw new Error(`Embedding endpoint returned status ${response.status}`)
    }
    const payload = (await response.json()) as EmbedResponsePayload
    return payload.embeddings || []
  }

  const toCondensedMessage = (msg: Message): string => {
    const roleLabel = msg.role === 'user' ? 'Me' : msg.model || 'AI'
    const timeLabel = msg.createdAt ? formatMessageTimestamp(msg.createdAt) : 'Unknown time'
    const compactContent = msg.content.replace(/\s+/g, ' ').trim().slice(0, 220)
    return `${roleLabel} - ${timeLabel} - ${compactContent}`
  }

  const buildCondensedContext = async (question: string): Promise<string[]> => {
    const sameProjectSessions = sessions.filter((s) => (s.projectId || GENERAL_PROJECT_ID) === activeProjectId)
    const history = sameProjectSessions.flatMap((s) => s.messages).filter((m) => m.content.trim())
    if (!history.length) return []

    let queryEmbedding: number[] | null = null
    try {
      const vectors = await embedTexts([question])
      queryEmbedding = vectors[0] ?? null
    } catch {
      queryEmbedding = null
    }

    const ranked = history.map((msg) => {
      const condensed = msg.condensed || toCondensedMessage(msg)
      const similarity = queryEmbedding && msg.embedding ? cosineSimilarity(queryEmbedding, msg.embedding) : -1
      return { condensed, similarity }
    })

    const withSimilarity = ranked.filter((entry) => entry.similarity >= 0).sort((a, b) => b.similarity - a.similarity)
    if (withSimilarity.length > 0) {
      return withSimilarity.slice(0, 8).map((entry) => entry.condensed)
    }
    return ranked.slice(-8).map((entry) => entry.condensed)
  }

  const createSession = () => {
    const now = new Date().toISOString()
    const id = `session_${Date.now()}`
    const next: ChatSession = {
      id,
      title: 'New Conversation',
      createdAt: now,
      messages: [],
      starred: false,
      isCustomTitle: false,
      projectId: GENERAL_PROJECT_ID,
    }
    setSessions((prev) => [next, ...prev])
    setActiveSessionId(id)
    setMainSection('chat')
    if (token) {
      fetch(`${getApiUrl()}/sessions/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ id, title: 'New Conversation', project_id: GENERAL_PROJECT_ID }),
      }).catch(() => { /* best-effort */ })
    }
  }

  const createProject = () => {
    const name = newProjectName.trim()
    if (!name) return
    const id = `project_${Date.now()}`
    setProjects((prev) => [...prev, { id, name: name.slice(0, 60) }])
    setNewProjectName('')
  }

  const moveSessionToProject = (sessionId: string, projectId: string) => {
    setSessions((prev) => prev.map((s) => (s.id === sessionId ? { ...s, projectId } : s)))
  }

  const saveSettings = () => {
    localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings))
    setProvider(settings.defaultProvider || provider)
    if (settings.defaultModel) {
      setModel(settings.defaultModel)
    }
    setSettingsStatus('Settings saved')
  }

  const saveCurrentPromptPreset = () => {
    const selected = promptPresets.find((preset) => preset.id === selectedPromptPresetId)
    if (!selected) return
    const name = editingPresetName.trim().slice(0, 60) || selected.name
    setPromptPresets((prev) =>
      prev.map((preset) =>
        preset.id === selected.id ? { ...preset, name, prompt: settings.systemPrompt } : preset
      )
    )
    setSettingsStatus(`Prompt preset '${name}' updated`)
  }

  const createPromptPreset = () => {
    const name = newPromptName.trim()
    if (!name) return
    const id = `prompt_${Date.now()}`
    const preset: PromptPreset = {
      id,
      name: name.slice(0, 60),
      creatorName: 'System',
      prompt: '',
    }
    setPromptPresets((prev) => [...prev, preset])
    setSelectedPromptPresetId(id)
    setPromptPage(Math.max(1, Math.ceil((promptPresets.length + 1) / PROMPTS_PAGE_SIZE)))
    setNewPromptName('')
    setSettingsStatus(`Prompt preset '${preset.name}' created`)
  }

  const deleteSelectedPromptPreset = () => {
    if (selectedPromptPresetId === 'prompt_default') {
      setSettingsStatus('Default preset cannot be deleted')
      return
    }
    const remaining = promptPresets.filter((preset) => preset.id !== selectedPromptPresetId)
    setPromptPresets(remaining)
    const fallback = remaining[0]?.id || 'prompt_default'
    setSelectedPromptPresetId(fallback)
    setPromptPage(1)
    setSettingsStatus('Prompt preset deleted')
  }

  const assignPromptToActiveConversation = (presetId: string) => {
    if (!activeSessionId || !presetId) return
    setSessions((prev) =>
      prev.map((session) =>
        session.id === activeSessionId ? { ...session, promptPresetId: presetId } : session
      )
    )
    setSettingsStatus('Prompt assigned to active conversation')
  }

  const downloadOllamaModel = async () => {
    const modelName = ollamaPullModel.trim()
    if (!modelName) return
    setOllamaPullStatus(`Downloading ${modelName}...`)
    try {
      const response = await fetch(`${getApiUrl()}/pull/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ model: modelName }),
      })
      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `HTTP ${response.status}`)
      }
      setOllamaPullStatus(`Downloaded ${modelName}`)
      setOllamaPullModel('')
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unknown error'
      setOllamaPullStatus(`Download failed: ${message}`)
    }
  }

  const deleteSession = (id: string) => {
    setSessions((prev) => prev.filter((session) => session.id !== id))
    if (id === activeSessionId) {
      const remaining = sessions.filter((session) => session.id !== id)
      if (remaining.length > 0) {
        setActiveSessionId(remaining[0].id)
      } else {
        createSession()
      }
    }
    if (token) {
      fetch(`${getApiUrl()}/sessions/${id}/`, {
        method: 'DELETE',
        headers: { ...authHeaders },
      }).catch(() => { /* best-effort */ })
    }
  }

  const toggleStarSession = (id: string) => {
    setSessions((prev) =>
      prev.map((session) => (session.id === id ? { ...session, starred: !session.starred } : session))
    )
    if (token) {
      const session = sessions.find((s) => s.id === id)
      if (session) {
        fetch(`${getApiUrl()}/sessions/${id}/`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ starred: !session.starred }),
        }).catch(() => { /* best-effort */ })
      }
    }
  }

  const beginEditTitle = () => {
    if (!activeSession) return
    setEditingTitleValue(activeSession.title)
    setIsEditingTitle(true)
  }

  const saveEditedTitle = () => {
    if (!activeSession) return
    const trimmed = editingTitleValue.trim()
    if (!trimmed) {
      setEditingTitleValue(activeSession.title)
      setIsEditingTitle(false)
      return
    }
    const newTitle = trimmed.slice(0, 100)
    setSessions((prev) =>
      prev.map((session) =>
        session.id === activeSession.id
          ? { ...session, title: newTitle, isCustomTitle: true }
          : session
      )
    )
    setIsEditingTitle(false)
    if (token) {
      fetch(`${getApiUrl()}/sessions/${activeSession.id}/`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ title: newTitle }),
      }).catch(() => { /* best-effort */ })
    }
  }

  const cancelEditTitle = () => {
    setIsEditingTitle(false)
    setEditingTitleValue(activeSession?.title ?? '')
  }

  const handleTitleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') { e.preventDefault(); saveEditedTitle() }
    if (e.key === 'Escape') { e.preventDefault(); cancelEditTitle() }
  }

  const loadSessionsFromLocalStorage = () => {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY)
    if (raw) {
      try {
        const parsed = JSON.parse(raw) as ChatSession[]
        if (parsed.length > 0) {
          const hydrated = parsed.map((session) => ({
            ...session,
            starred: session.starred ?? false,
            isCustomTitle: session.isCustomTitle ?? false,
            projectId: session.projectId || GENERAL_PROJECT_ID,
            promptPresetId: session.promptPresetId,
            messages: session.messages.map((msg) => ({
              ...msg,
              createdAt: msg.createdAt || session.createdAt,
              condensed: msg.condensed || toCondensedMessage({ ...msg, createdAt: msg.createdAt || session.createdAt }),
            })),
          }))
          setSessions(hydrated)
          setActiveSessionId(parsed[0].id)
          return true
        }
      } catch {
        localStorage.removeItem(SESSION_STORAGE_KEY)
      }
    }
    return false
  }

  useEffect(() => {
    if (!token) {
      if (!loadSessionsFromLocalStorage()) createSession()
      return
    }

    const loadFromBackend = async () => {
      try {
        const res = await fetch(`${getApiUrl()}/sessions/`, {
          headers: { ...authHeaders },
        })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const data = await res.json() as { sessions: Array<{
          id: string; title: string; project_id: string | null; is_starred: boolean;
          last_model: string | null; last_provider: string | null; created_at: string; updated_at: string;
        }> }
        const serverSessions = data.sessions || []

        // Build a map of localStorage sessions for merging messages
        const localMap = new Map<string, ChatSession>()
        try {
          const raw = localStorage.getItem(SESSION_STORAGE_KEY)
          if (raw) {
            const parsed = JSON.parse(raw) as ChatSession[]
            parsed.forEach((s) => localMap.set(s.id, s))
          }
        } catch { /* ignore */ }

        // Build merged list: backend sessions (with messages from localStorage) + any
        // localStorage-only sessions (not yet synced to backend, e.g. 0-turn sessions)
        const backendIds = new Set(serverSessions.map((s) => s.id))
        const fromBackend: ChatSession[] = serverSessions.map((s) => {
          const local = localMap.get(s.id)
          return {
            id: s.id,
            title: s.title || 'New Conversation',
            createdAt: s.created_at,
            messages: local?.messages ?? [],
            starred: s.is_starred,
            isCustomTitle: !!(s.title && s.title !== 'New Conversation'),
            projectId: s.project_id || GENERAL_PROJECT_ID,
          }
        })

        // Prepend any localStorage sessions not yet on the backend (no chat turns yet)
        const localOnly: ChatSession[] = Array.from(localMap.values())
          .filter((s) => !backendIds.has(s.id))

        const merged = [...localOnly, ...fromBackend]

        if (merged.length === 0) {
          createSession()
          return
        }

        setSessions(merged)
        setActiveSessionId(merged[0].id)
      } catch {
        if (!loadSessionsFromLocalStorage()) createSession()
      }
    }

    loadFromBackend()
  }, [token])

  useEffect(() => { localStorage.setItem(PROVIDER_STORAGE_KEY, provider) }, [provider])
  useEffect(() => { localStorage.setItem(MODEL_STORAGE_KEY, model) }, [model])
  useEffect(() => { localStorage.setItem(SCOPE_STORAGE_KEY, queryScope) }, [queryScope])
  useEffect(() => { localStorage.setItem(DOC_SIDEBAR_STORAGE_KEY, JSON.stringify(docSidebar)) }, [docSidebar])

  useEffect(() => {
    if (sessions.length > 0) {
      const stripped = sessions.map(s => ({
        ...s,
        messages: s.messages.map(m => ({
          ...m,
          images: m.images?.map(() => '[image]'),
          docs: m.docs?.map(d => ({ name: d.name, serverId: d.serverId })),
          embedding: undefined,
        })),
      }))
      try {
        localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(stripped))
      } catch {
        // quota exceeded — trim oldest sessions and retry
        const trimmed = stripped.slice(-10)
        try { localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(trimmed)) } catch { /* give up */ }
      }
    }
  }, [sessions])

  useEffect(() => { localStorage.setItem(PROJECT_STORAGE_KEY, JSON.stringify(projects)) }, [projects])
  useEffect(() => { localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(settings)) }, [settings])

  useEffect(() => {
    setIsEditingTitle(false)
    setEditingTitleValue(activeSession?.title ?? '')
  }, [activeSessionId])

  // Lazily load messages for sessions that came from the backend with empty messages
  useEffect(() => {
    if (!activeSessionId || !token || !authHeaders) return
    const session = sessions.find((s) => s.id === activeSessionId)
    if (!session || session.messages.length > 0) return

    fetch(`${getApiUrl()}/sessions/${activeSessionId}/`, { headers: { ...authHeaders } })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => {
        if (!data || !Array.isArray(data.turns) || data.turns.length === 0) return
        const loadedMessages: Message[] = data.turns.map((t: {
          role: 'user' | 'assistant'; content: string; model?: string; sources?: string[]; created_at?: string;
        }) => ({
          role: t.role,
          content: t.content,
          model: t.model,
          sources: Array.isArray(t.sources) ? t.sources : [],
          createdAt: t.created_at,
        }))
        setSessions((prev) => prev.map((s) =>
          s.id === activeSessionId ? { ...s, messages: loadedMessages } : s
        ))
      })
      .catch(() => { /* best-effort */ })
  }, [activeSessionId, token])

  useEffect(() => {
    localStorage.setItem(PROMPT_PRESETS_STORAGE_KEY, JSON.stringify(promptPresets))
  }, [promptPresets])

  useEffect(() => {
    localStorage.setItem(ACTIVE_PROMPT_PRESET_STORAGE_KEY, selectedPromptPresetId)
  }, [selectedPromptPresetId])

  useEffect(() => {
    const selected = promptPresets.find((preset) => preset.id === selectedPromptPresetId)
    if (!selected) return
    setSettings((prev) => ({ ...prev, systemPrompt: selected.prompt }))
    setEditingPresetName(selected.name)
  }, [selectedPromptPresetId, promptPresets])

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const response = await fetch(`${getApiUrl()}/models/`, { headers: { ...authHeaders } })
        if (response.ok) {
          const data = (await response.json()) as ModelsResponsePayload
          const parsedModels: ModelOption[] = (data.models || [])
            .map((m) => ({ name: m.name, provider: m.provider || detectProvider(m.name) }))
            .filter((m) => Boolean(m.name))
          setAvailableModels(parsedModels)

          if (data.default_provider && !settings.defaultProvider) {
            setSettings((prev) => ({ ...prev, defaultProvider: data.default_provider || prev.defaultProvider }))
          }
          if (data.default_model && !settings.defaultModel) {
            setSettings((prev) => ({ ...prev, defaultModel: data.default_model || prev.defaultModel }))
          }

          const providerList = [...new Set(parsedModels.map((m) => m.provider))]
          if (providerList.length > 0 && !providerList.includes(provider)) {
            setProvider(settings.defaultProvider && providerList.includes(settings.defaultProvider) ? settings.defaultProvider : providerList[0])
          }

          const providerModels = parsedModels.filter((m) => m.provider === (providerList.includes(provider) ? provider : providerList[0]))
          const providerModelNames = providerModels.map((m) => m.name)
          if (providerModelNames.length > 0 && !providerModelNames.includes(model)) {
            if (settings.defaultModel && providerModelNames.includes(settings.defaultModel)) {
              setModel(settings.defaultModel)
            } else {
              setModel(providerModelNames[0])
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch models:', error)
        setAvailableModels([{ name: 'llama3.2:1b', provider: 'ollama' }])
      }
    }
    fetchModels()
  }, [model, provider, settings.defaultModel, settings.defaultProvider])

  useEffect(() => {
    const modelsForProvider = availableModels.filter((m) => m.provider === provider)
    if (modelsForProvider.length > 0 && !modelsForProvider.some((m) => m.name === model)) {
      setModel(modelsForProvider[0].name)
    }
  }, [provider, availableModels, model])

  const scrollToBottom = () => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }
  useEffect(() => { scrollToBottom() }, [messages])

  const uploadDocumentInBackground = (att: Attachment) => {
    const nowIso = new Date().toISOString()
    setDocSidebar((prev) => [
      { id: att.id, name: att.name, status: 'uploading', addedAt: nowIso, sessionId: activeSessionId },
      ...prev,
    ])
    setDocSidebarOpen(true)

    const updatePill = (patch: Partial<Attachment>) =>
      setPendingAttachments((prev) => prev.map((a) => (a.id === att.id ? { ...a, ...patch } : a)))
    const updateSidebar = (patch: Partial<DocSidebarEntry>) =>
      setDocSidebar((prev) => prev.map((e) => (e.id === att.id ? { ...e, ...patch } : e)))

    updatePill({ status: 'uploading' })

    const form = new FormData()
    form.append('collection', 'user_documents')
    form.append('file', att.file, att.name)

    fetch(`${getApiUrl()}/upload/receive/`, { method: 'POST', body: form, headers: { ...authHeaders } })
      .then(async (resp) => {
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}))
          const errMsg = err.error ?? `HTTP ${resp.status}`
          updatePill({ status: 'error', error: errMsg })
          updateSidebar({ status: 'error', error: errMsg })
          return
        }
        const { temp_id } = await resp.json()

        updatePill({ status: 'ingesting', progress: 0 })
        updateSidebar({ status: 'ingesting', progress: 0 })

        const r2 = await fetch(`${getApiUrl()}/upload/ingest/`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ temp_id, model, provider }),
        })

        if (!r2.ok || !r2.body) {
          const err = await r2.json().catch(() => ({}))
          const errMsg = (err as {error?: string}).error ?? `HTTP ${r2.status}`
          updatePill({ status: 'error', error: errMsg })
          updateSidebar({ status: 'error', error: errMsg })
          return
        }

        const reader = r2.body.getReader()
        const decoder = new TextDecoder()
        let buffer = ''
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          buffer += decoder.decode(value, { stream: true })
          const lines = buffer.split('\n')
          buffer = lines.pop() ?? ''
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            let event: Record<string, unknown>
            try { event = JSON.parse(line.slice(6)) } catch { continue }
            if (typeof event.error === 'string') {
              updatePill({ status: 'error', error: event.error })
              updateSidebar({ status: 'error', error: event.error })
              return
            }
            if (typeof event.progress === 'number') {
              updatePill({ status: 'ingesting', progress: event.progress })
              updateSidebar({ status: 'ingesting', progress: event.progress })
            }
            if (event.done === true) {
              const chunks = typeof event.chunks_ingested === 'number' ? event.chunks_ingested : 0
              const pages = typeof event.pages_ingested === 'number' ? event.pages_ingested : 0
              const documentId = typeof event.document_id === 'string' ? event.document_id : undefined
              updatePill({ status: 'done', chunks, pages, progress: 100 })
              updateSidebar({ status: 'done', chunks, pages, progress: 100, documentId })
            }
          }
        }
      })
      .catch((err) => {
        const errMsg = String(err)
        updatePill({ status: 'error', error: errMsg })
        updateSidebar({ status: 'error', error: errMsg })
      })
  }

  const addAttachments = (files: FileList | File[]) => {
    const fileArr = Array.from(files)
    const newAttachments: Attachment[] = []
    for (const file of fileArr) {
      const isImage = file.type.startsWith('image/')
      const isDoc = /\.(pdf|docx|txt)$/i.test(file.name)
      if (!isImage && !isDoc) continue
      const att: Attachment = {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        type: isImage ? 'image' : 'document',
        name: file.name,
        file,
        status: 'pending',
      }
      const reader = new FileReader()
      reader.onload = (e) => {
        const dataUrl = e.target?.result as string
        setPendingAttachments((prev) => prev.map((a) => (a.id === att.id ? { ...a, dataUrl } : a)))
        // Upload to server for persistent storage (best-effort, non-blocking)
        const formData = new FormData()
        formData.append('file', att.file)
        if (activeSessionId) formData.append('session_id', activeSessionId)
        fetch(`${getApiUrl()}/attachments/`, {
          method: 'POST',
          headers: { Authorization: `Token ${token || ''}` },
          body: formData,
        })
          .then(r => r.ok ? r.json() : null)
          .then(data => {
            if (data?.id) {
              setPendingAttachments(prev => prev.map(a => a.id === att.id ? { ...a, serverId: data.id } : a))
            }
          })
          .catch(() => { /* best-effort */ })
      }
      reader.readAsDataURL(file)
      newAttachments.push(att)
    }
    if (newAttachments.length) {
      setPendingAttachments((prev) => [...prev, ...newAttachments])
      for (const att of newAttachments) {
        if (att.type === 'document') {
          uploadDocumentInBackground(att)
        }
      }
    }
  }

  const removeAttachment = (id: string) => {
    setPendingAttachments((prev) => prev.filter((a) => a.id !== id))
  }

  const handleChatDragOver = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(true) }
  const handleChatDragLeave = (e: React.DragEvent) => { e.preventDefault(); e.stopPropagation(); setIsDragOver(false) }
  const handleChatDrop = (e: React.DragEvent) => {
    e.preventDefault(); e.stopPropagation(); setIsDragOver(false)
    if (e.dataTransfer.files.length) addAttachments(e.dataTransfer.files)
  }

  const handleTextareaPaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (e.clipboardData.files.length) {
      const imageFiles = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith('image/'))
      if (imageFiles.length) { e.preventDefault(); addAttachments(imageFiles) }
    }
  }

  // Background ingestion (uploadDocumentInBackground) can still be mid-flight
  // ('uploading' or 'ingesting') when the user hits Send right after
  // attaching a document. Sending immediately meant the chat request fired
  // before the document's chunks existed in Qdrant at all -- retrieval found
  // nothing, the model fell back to generic knowledge, and the only
  // "content" it actually had was the filename text injected into the
  // prompt. Poll the live attachment status (via the ref, since the
  // `attachedDocFiles` array captured at the top of sendMessage is a stale
  // snapshot) until ingestion finishes or a timeout elapses, so the chat
  // request only fires once real chunks are searchable.
  const waitForAttachmentReady = async (attId: string, timeoutMs = 90_000): Promise<Attachment | undefined> => {
    const startedAt = Date.now()
    while (Date.now() - startedAt < timeoutMs) {
      const current = pendingAttachmentsRef.current.find((a) => a.id === attId)
      if (!current || current.status === 'done' || current.status === 'error') return current
      setStatusText(`Waiting for ${current.name} to finish indexing…`)
      await sleep(500)
    }
    return pendingAttachmentsRef.current.find((a) => a.id === attId)
  }

  const sendMessage = async () => {
    if ((!input.trim() && pendingAttachments.length === 0) || loading || !activeSessionId) return

    const nowIso = new Date().toISOString()
    const imageAttachments = pendingAttachments.filter((a) => a.type === 'image')
    const docAttachments = pendingAttachments.filter((a) => a.type === 'document')
    const imageDataUrls = imageAttachments.map((a) => a.dataUrl).filter(Boolean) as string[]

    const userMessage: Message = {
      role: 'user',
      content: input,
      createdAt: nowIso,
      images: imageDataUrls.length ? imageDataUrls : undefined,
      docs: docAttachments.map((a) => ({ name: a.name, dataUrl: a.dataUrl, serverId: a.serverId })),
      condensed: `Me - ${formatMessageTimestamp(nowIso)} - ${input.replace(/\s+/g, ' ').trim().slice(0, 220)}`,
    }

    setPendingAttachments([])
    updateActiveMessages((prev) => [
      ...prev,
      userMessage,
      { role: 'assistant', content: '', createdAt: nowIso, model, condensed: `${model} - ${formatMessageTimestamp(nowIso)} - pending response` },
    ])
    const assistantMessageIndex = messages.length + 1
    const userMessageIndex = messages.length

    setInput('')
    setLoading(true)
    setStatusText('Retrieving context and generating response')

    const requestId = ++requestCounterRef.current
    const requestStartedAt = performance.now()
    const debugLog = (detail: string) => {
      const line = `[#${requestId}] ${detail}`
      setDebugText(line)
      console.warn(`[ServerLLM UI] ${line}`)
    }

    abortControllerRef.current = new AbortController()
    let requestTimedOut = false
    const timeoutId = window.setTimeout(() => {
      requestTimedOut = true
      abortControllerRef.current?.abort()
    }, REQUEST_TIMEOUT_MS)

    const messageText = input
    const attachedDocFiles = docAttachments
    const attachedImageUrls = imageDataUrls
    const effectiveSystemPrompt = (() => {
      if (activeSession?.promptPresetId) {
        const preset = promptPresets.find((entry) => entry.id === activeSession.promptPresetId)
        if (preset) return preset.prompt
      }
      return settings.systemPrompt
    })()

    let conversationContext: string[] = []
    try {
      conversationContext = await buildCondensedContext(messageText)
    } catch {
      conversationContext = []
    }

    try {
      const vectors = await embedTexts([messageText])
      const embedding = vectors[0]
      if (embedding?.length) {
        patchActiveMessageAtIndex(userMessageIndex, { embedding, condensed: userMessage.condensed })
      }
    } catch { /* best-effort */ }

    let uploadedDocNotes: string[] = []
    for (const att of attachedDocFiles) {
      if (att.status === 'done') {
        uploadedDocNotes.push(`${att.name} (${att.pages ? att.pages + ' pages' : (att.chunks ?? 0) + ' chunks'} indexed)`)
        continue
      }
      if (att.status === 'uploading' || att.status === 'ingesting' || att.status === 'pending') {
        const finished = await waitForAttachmentReady(att.id)
        if (finished?.status === 'done') {
          uploadedDocNotes.push(`${finished.name} (${finished.pages ? finished.pages + ' pages' : (finished.chunks ?? 0) + ' chunks'} indexed)`)
        } else if (finished?.status === 'error') {
          uploadedDocNotes.push(`${finished.name} (indexing failed: ${finished.error ?? 'unknown error'})`)
        } else {
          uploadedDocNotes.push(`${att.name} (indexing still in progress after 90s — query may include partial or no context for this document)`)
        }
        continue
      }
      try {
        setStatusText(`Ingesting ${att.name}…`)
        const form = new FormData()
        form.append('collection', 'user_documents')
        form.append('file', att.file, att.name)
        const resp = await fetch(`${getApiUrl()}/upload/`, { method: 'POST', body: form, headers: { ...authHeaders } })
        if (!resp.ok) {
          const err = await resp.json().catch(() => ({}))
          uploadedDocNotes.push(`${att.name} (upload failed: ${err.error ?? resp.status})`)
        } else {
          const result = await resp.json()
          uploadedDocNotes.push(`${att.name} (${result.chunks_ingested ?? 0} chunks indexed)`)
        }
      } catch (err) {
        uploadedDocNotes.push(`${att.name} (upload failed: ${String(err)})`)
      }
    }
    setStatusText('Retrieving context and generating response')

    const collectionHints = queryScope === 'training'
      ? ['pattern_context_library']
      : attachedDocFiles.length > 0 ? ['user_documents'] : undefined
    const collections = collectionHints

    const fullMessageText = uploadedDocNotes.length
      ? `${messageText}\n\n[Documents indexed for this query: ${uploadedDocNotes.join(', ')}]`
      : messageText

    debugLog(`sending POST ${getApiUrl()}/chat/ model=${model} provider=${provider} scope=${queryScope} msgLen=${fullMessageText.length} images=${attachedImageUrls.length}`)

    const animateAssistantText = async (fullText: string, finalSources: string[], responseModel: string) => {
      const length = fullText.length
      const chunkSize = Math.max(4, Math.min(22, Math.floor(length / 120) || 6))
      for (let index = chunkSize; index <= length; index += chunkSize) {
        const partial = fullText.slice(0, index)
        updateActiveMessages((prev) => {
          const next = [...prev]
          if (next[assistantMessageIndex]) {
            next[assistantMessageIndex] = { ...next[assistantMessageIndex], role: 'assistant', content: partial }
          }
          return next
        })
        await sleep(10)
      }
      updateActiveMessages((prev) => {
        const next = [...prev]
        if (next[assistantMessageIndex]) {
          next[assistantMessageIndex] = { ...next[assistantMessageIndex], role: 'assistant', content: fullText, sources: finalSources, model: responseModel }
        }
        return next
      })
    }

    try {
      const pinnedEntries = docSidebar.filter((e) => e.sessionId === activeSessionId && e.status === 'done')
      const pinnedDocumentIds = pinnedEntries.filter((e) => e.documentId).map((e) => e.documentId as string)
      const pinnedFilenames = pinnedEntries.map((e) => e.name)
      const hasPinned = pinnedEntries.length > 0

      const response = await fetch(`${getApiUrl()}/chat/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          message: fullMessageText,
          images: attachedImageUrls.length ? attachedImageUrls : undefined,
          model: model,
          provider,
          system_prompt: effectiveSystemPrompt,
          max_results: hasPinned ? Math.max(30, pinnedEntries.length * 15) : 20,
          include_graph: false,
          include_web: includeWeb,
          collections,
          conversation_context: conversationContext,
          session_id: activeSessionId,
          turn_id: `${activeSessionId}_${Date.now()}`,
          pinned_document_ids: pinnedDocumentIds.length ? pinnedDocumentIds : undefined,
          pinned_filenames: pinnedFilenames.length ? pinnedFilenames : undefined,
        }),
        signal: abortControllerRef.current.signal
      })

      debugLog(`response status=${response.status} ok=${response.ok}`)

      if (!response.ok) {
        let apiError = 'Failed to get response'
        try {
          const payload = (await response.json()) as ChatResponsePayload
          if (payload.error) apiError = payload.error
        } catch { /* ignore */ }
        throw new Error(apiError)
      }

      let accumulatedContent = ''
      let sources: string[] = []
      const contentType = response.headers.get('content-type') || ''
      debugLog(`content-type=${contentType || 'none'}`)

      if (contentType.includes('application/json')) {
        const payload = (await response.json()) as ChatResponsePayload
        accumulatedContent = payload.answer || ''
        sources = payload.sources || []
        const responseModel = payload.model || model
        debugLog(`json payload answerLen=${accumulatedContent.length} sources=${sources.length}`)
        await animateAssistantText(accumulatedContent, sources, responseModel)
        try {
          const vectors = await embedTexts([accumulatedContent])
          const embedding = vectors[0]
          if (embedding?.length) {
            patchActiveMessageAtIndex(assistantMessageIndex, {
              embedding,
              condensed: `${responseModel} - ${formatMessageTimestamp(nowIso)} - ${accumulatedContent.replace(/\s+/g, ' ').trim().slice(0, 220)}`,
            })
          }
        } catch { /* best-effort */ }
        setStatusText('Response complete')
        debugLog(`completed in ${Math.round(performance.now() - requestStartedAt)}ms (json)`)
        return
      }

      const reader = response.body?.getReader()
      const decoder = new TextDecoder()

      if (reader) {
        let streamBuffer = ''
        let streamFinished = false
        debugLog('stream reader opened')

        while (true) {
          const { done, value } = await reader.read()
          if (done) { debugLog('stream reader done=true'); break }

          streamBuffer += decoder.decode(value, { stream: true })
          const lines = streamBuffer.split('\n')
          streamBuffer = lines.pop() ?? ''

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const payload = line.slice(6).trim()
              if (!payload) continue
              let data: { token?: string; done?: boolean; sources?: string[]; error?: string }
              try {
                data = JSON.parse(payload) as { token?: string; done?: boolean; sources?: string[]; error?: string }
              } catch { continue }
              if (data.error) { debugLog(`stream error=${data.error}`); throw new Error(data.error) }
              if (data.token) {
                accumulatedContent += data.token
                updateActiveMessages((prev) => {
                  const newMessages = [...prev]
                  if (newMessages[assistantMessageIndex]) {
                    newMessages[assistantMessageIndex] = { ...newMessages[assistantMessageIndex], role: 'assistant', content: accumulatedContent }
                  }
                  return newMessages
                })
              }
              if (data.done) {
                sources = data.sources || []
                updateActiveMessages((prev) => {
                  const newMessages = [...prev]
                  if (newMessages[assistantMessageIndex]) {
                    newMessages[assistantMessageIndex] = { ...newMessages[assistantMessageIndex], role: 'assistant', content: accumulatedContent, sources, model }
                  }
                  return newMessages
                })
                setStatusText('Response complete')
                debugLog(`stream done received sources=${sources.length}`)
                streamFinished = true
                break
              }
            }
          }
          if (streamFinished) break
        }

        if (!streamFinished && accumulatedContent.trim()) {
          setStatusText('Response complete')
          debugLog('stream ended without explicit done, content present')
        }

        if (accumulatedContent.trim()) {
          try {
            const vectors = await embedTexts([accumulatedContent])
            const embedding = vectors[0]
            if (embedding?.length) {
              patchActiveMessageAtIndex(assistantMessageIndex, {
                embedding,
                condensed: `${model} - ${formatMessageTimestamp(nowIso)} - ${accumulatedContent.replace(/\s+/g, ' ').trim().slice(0, 220)}`,
              })
            }
          } catch { /* best-effort */ }
        }

        if (!streamFinished && !accumulatedContent.trim()) {
          throw new Error('No response content was returned by the server stream.')
        }
      } else {
        throw new Error('Response body stream was not available in the browser response.')
      }
    } catch (error) {
      console.error('Error:', error)
      if (error instanceof Error && error.name === 'AbortError') {
        if (requestTimedOut) {
          updateActiveMessages((prev) => {
            const newMessages = [...prev]
            if (newMessages[assistantMessageIndex]) {
              const base = (newMessages[assistantMessageIndex].content || '').trim()
              newMessages[assistantMessageIndex] = { ...newMessages[assistantMessageIndex], role: 'assistant', content: base ? `${base}\n\n[Request timed out]` : '[Request timed out]' }
            }
            return newMessages
          })
          setStatusText('Request timed out')
          debugLog(`request timed out after ${REQUEST_TIMEOUT_MS}ms`)
        } else {
          updateActiveMessages((prev) => {
            const newMessages = [...prev]
            if (newMessages[assistantMessageIndex]) {
              newMessages[assistantMessageIndex] = { ...newMessages[assistantMessageIndex], role: 'assistant', content: `${newMessages[assistantMessageIndex].content}\n\n[Response stopped by user]` }
            }
            return newMessages
          })
          setStatusText('Generation stopped')
          debugLog('request aborted by user')
        }
      } else {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error'
        updateActiveMessages((prev) => {
          const newMessages = [...prev]
          if (newMessages[assistantMessageIndex]) {
            newMessages[assistantMessageIndex] = { ...newMessages[assistantMessageIndex], role: 'assistant', content: `Sorry, there was an error processing your request.\n\n${errorMessage}`, model }
          }
          return newMessages
        })
        setStatusText('Error while generating response')
        debugLog(`error=${errorMessage}`)
      }
    } finally {
      window.clearTimeout(timeoutId)
      setLoading(false)
      abortControllerRef.current = null
      requestAnimationFrame(() => inputRef.current?.focus())
    }
  }

  const stopGeneration = () => { abortControllerRef.current?.abort() }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey && !e.nativeEvent.isComposing) {
      e.preventDefault()
      sendMessage()
    }
  }

  return (
    <div className={`app-shell${docSidebarOpen ? ' doc-sidebar-open' : ''}`}>
      <aside className="sidebar">
        <button className="new-chat" onClick={createSession}>
          + New Conversation
        </button>

        <div className="session-list">
          <div className="session-section">
            <h3 className="session-section-title">Projects</h3>
            {projects.map((project) => {
              const projectSessions = sessions.filter((s) => (s.projectId || GENERAL_PROJECT_ID) === project.id)
              return (
                <div
                  key={project.id}
                  className="project-group"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={() => {
                    if (draggedSessionId) moveSessionToProject(draggedSessionId, project.id)
                    setDraggedSessionId(null)
                  }}
                >
                  <div className="project-heading">{project.name}</div>
                  {projectSessions.map((session) => (
                    <div
                      key={session.id}
                      className={`session-item ${session.id === activeSessionId ? 'active' : ''}`}
                      draggable
                      onDragStart={() => setDraggedSessionId(session.id)}
                      onDragEnd={() => setDraggedSessionId(null)}
                    >
                      <button className="session-open" onClick={() => { setActiveSessionId(session.id); setMainSection('chat') }}>
                        <span className="session-title">{session.title}</span>
                        <span className="session-meta">{session.messages.length} messages</span>
                      </button>
                      <div className="session-actions">
                        <button className="session-star" onClick={() => toggleStarSession(session.id)} aria-label={session.starred ? 'Unstar' : 'Star'} title={session.starred ? 'Unstar' : 'Star'}>
                          {session.starred ? '★' : '☆'}
                        </button>
                        <button className="session-delete" onClick={() => deleteSession(session.id)} aria-label="Delete conversation">x</button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            })}

            <div className="project-create-row">
              <input value={newProjectName} onChange={(e) => setNewProjectName(e.target.value)} placeholder="New project name" aria-label="New project name" />
              <button onClick={createProject}>Add</button>
            </div>
          </div>
        </div>

        <div className="sidebar-footer-actions">
          <button className="new-chat settings-open" onClick={() => setMainSection('settings')}>Settings</button>
        </div>
      </aside>

      <main
        className={`chat-main ${settings.enableBackgrounds ? 'background-enabled' : ''}`}
      >
        <header className="chat-header">
          <div className="header-controls">
            <div className="control-group">
              <label htmlFor="queryScope">Data Scope</label>
              <select id="queryScope" value={queryScope} onChange={(e) => setQueryScope(e.target.value as QueryScope)} disabled={loading}>
                <option value="all">All data</option>
                <option value="training">Training history only</option>
              </select>
            </div>

            <div className="control-group">
              <label htmlFor="provider">Provider</label>
              <select id="provider" value={provider} onChange={(e) => setProvider(e.target.value)} disabled={loading}>
                {[...new Set(availableModels.map((m) => m.provider))].map((providerName) => (
                  <option key={providerName} value={providerName}>{providerName}</option>
                ))}
              </select>
            </div>

            <div className="control-group">
              <label htmlFor="model">Model</label>
              <select id="model" value={model} onChange={(e) => setModel(e.target.value)} disabled={loading}>
                {availableModels.filter((m) => m.provider === provider).length > 0 ? (
                  availableModels.filter((m) => m.provider === provider).map((modelOption) => (
                    <option key={modelOption.name} value={modelOption.name}>{modelOption.name}</option>
                  ))
                ) : (
                  <option value="llama3.2:1b">Loading models...</option>
                )}
              </select>
            </div>

            {mainSection === 'chat' && (
              <div className="control-group">
                <label htmlFor="conversationPrompt">Conversation Prompt</label>
                <select id="conversationPrompt" value={activeConversationPromptPresetId} onChange={(e) => assignPromptToActiveConversation(e.target.value)} disabled={!activeSessionId}>
                  {promptPresets.map((preset) => (
                    <option key={preset.id} value={preset.id}>{preset.name}</option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="active-session-title-panel">
            {mainSection === 'chat' ? (
              isEditingTitle ? (
                <div className="active-session-title-editing">
                  <input className="active-session-title-input" value={editingTitleValue} onChange={(e) => setEditingTitleValue(e.target.value)} onKeyDown={handleTitleInputKeyDown} aria-label="Edit conversation title" maxLength={100} autoFocus />
                  <button className="title-edit-action" onClick={saveEditedTitle}>Save</button>
                  <button className="title-edit-action secondary" onClick={cancelEditTitle}>Cancel</button>
                </div>
              ) : (
                <div className="active-session-title-view">
                  <h3 title={activeSession?.title || 'Conversation'}>{activeSession?.title || 'Conversation'}</h3>
                  <button className="title-edit-button" onClick={beginEditTitle} aria-label="Rename conversation" title="Rename conversation">✎</button>
                </div>
              )
            ) : (
              <div className="active-session-title-view">
                <h3>Settings</h3>
                <button className="title-edit-button" onClick={() => setMainSection('chat')}>Back To Chat</button>
              </div>
            )}
          </div>

          <button
            className={`doc-sidebar-toggle${docSidebarOpen ? ' open' : ''}${docSidebar.length > 0 ? ' has-docs' : ''}`}
            onClick={() => setDocSidebarOpen((v) => !v)}
            title={docSidebarOpen ? 'Hide document panel' : 'Show ingested documents'}
            aria-label="Toggle document sidebar"
          >
            <span /><span /><span />
            {docSidebar.some((e) => e.status === 'uploading' || e.status === 'ingesting') && (
              <span className="sidebar-toggle-spinner" />
            )}
          </button>
        </header>

        {mainSection === 'chat' ? (
          <>
            <section className="messages">
              {messages.length === 0 && (
                <div className="empty-state">
                  <p>Ask questions about your clinical documents, guidelines, or research papers.</p>
                  <p className="hint">Example: Summarise the key findings from the uploaded protocol document.</p>
                </div>
              )}

              {messages.map((msg, idx) => {
                const timestampText = formatMessageTimestamp(msg.createdAt)
                const hasMeta = Boolean(timestampText) || msg.role === 'assistant' || msg.role === 'user'
                return (
                  <div key={`${msg.role}-${idx}`} className={`message ${msg.role} ${hasMeta ? 'with-meta' : ''}`}>
                    <div className="message-content">
                      {hasMeta && (
                        <div className="message-meta-line">
                          {msg.role === 'user' && <span>{timestampText ? `${timestampText} - Me` : 'Me'}</span>}
                          {msg.role === 'assistant' && (
                            <span>{msg.model ? `${msg.model}${timestampText ? ` - ${timestampText}` : ''}` : `AI${timestampText ? ` - ${timestampText}` : ''}`}</span>
                          )}
                        </div>
                      )}
                      {msg.images && msg.images.length > 0 && (
                        <div className="message-image-strip">
                          {msg.images.filter(src => src !== '[image]').map((dataUrl, imgIdx) => (
                            <button key={imgIdx} className="message-image-thumb-link" title="Click to view full image" onClick={() => setLightbox({ dataUrl, name: `Image ${imgIdx + 1}`, type: 'image' })}>
                              <img src={dataUrl} alt={`Attached image ${imgIdx + 1}`} className="message-image-thumb" />
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="message-text">
                        {loading && msg.role === 'assistant' && idx === messages.length - 1 && !msg.content.trim() ? 'Thinking...' : msg.content}
                      </div>
                      {msg.sources && msg.sources.length > 0 && (
                        <details className="sources">
                          <summary>View Sources ({msg.sources.length})</summary>
                          <div className="sources-content">
                            {msg.sources.map((source, sourceIndex) => (
                              <pre key={`${sourceIndex}-${source.slice(0, 20)}`}>{source}</pre>
                            ))}
                          </div>
                        </details>
                      )}
                      {msg.docs && msg.docs.length > 0 && (
                        <div className="message-doc-strip">
                          {msg.docs.map((doc, docIdx) => (
                            <button key={docIdx} className="message-doc-thumb" title={`Click to view ${doc.name}`} onClick={() => {
                              if (doc.dataUrl) {
                                setLightbox({ dataUrl: doc.dataUrl, name: doc.name, type: 'doc' })
                              } else if (doc.serverId) {
                                fetch(`${getApiUrl()}/attachments/${doc.serverId}/`, {
                                  headers: { Authorization: `Token ${token || ''}` },
                                })
                                  .then(r => r.blob())
                                  .then(blob => {
                                    const url = URL.createObjectURL(blob)
                                    setLightbox({ dataUrl: url, name: doc.name, type: 'doc' })
                                  })
                                  .catch(() => {})
                              }
                            }}>
                              <span className="message-doc-icon">📎</span>
                              <span className="message-doc-name">{doc.name}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )
              })}

              <div ref={messagesEndRef} />
            </section>

            <footer className={`input-container${isDragOver ? ' drag-over' : ''}`} onDragOver={handleChatDragOver} onDragLeave={handleChatDragLeave} onDrop={handleChatDrop}>
              {isDragOver && (
                <div className="drop-overlay"><span>⤓ Drop to attach</span></div>
              )}
              {pendingAttachments.length > 0 && (
                <div className="attachment-pills">
                  {pendingAttachments.map((att) => (
                    <div key={att.id} className={`attachment-pill ${att.type} status-${att.status}`}>
                      {att.type === 'image' && att.dataUrl && <img src={att.dataUrl} alt={att.name} className="pill-thumb" />}
                      <div className="pill-top-row">
                        {att.type === 'document' && (
                          <span className="pill-status-icon" aria-hidden="true">
                            {att.status === 'uploading' && <span className="pill-spinner" title="Uploading…" />}
                            {att.status === 'ingesting' && <span style={{ color: '#22c55e' }} title="Uploaded — ingesting…">✓</span>}
                            {att.status === 'done' && <span style={{ color: '#22c55e' }}>✓</span>}
                            {att.status === 'error' && <span style={{ color: 'var(--danger)' }}>✗</span>}
                            {att.status === 'pending' && '📄'}
                          </span>
                        )}
                        <span className="pill-name" title={att.name}>{att.name}</span>
                        {att.status === 'done' && <span className="pill-chunks">{att.pages ? `${att.pages} pages` : `${att.chunks ?? 0} chunks`}</span>}
                        {att.status === 'error' && <span className="pill-error-text" title={att.error}>failed</span>}
                        <button className="pill-remove" onClick={() => removeAttachment(att.id)} aria-label={`Remove ${att.name}`}>×</button>
                      </div>
                      {(att.status === 'ingesting' || att.status === 'done') && (
                        <div className="pill-progress-row">
                          <div className="pill-progress-track">
                            <div className="pill-progress-fill" style={{ width: `${att.progress ?? 0}%` }} />
                          </div>
                          <span className="pill-pct">{att.progress ?? 0}%</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="input-box">
                <textarea ref={inputRef} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyPress} onPaste={handleTextareaPaste} placeholder="Ask about your documents, clinical guidelines, or research…  Drop a PDF/image or paste a screenshot" rows={3} disabled={loading} />
                <div className="actions">
                  <label className="web-search-toggle" title="Search the web for up-to-date information">
                    <input type="checkbox" checked={includeWeb} onChange={(e) => setIncludeWeb(e.target.checked)} disabled={loading} />
                    Web search
                  </label>
                  {loading ? (
                    <button onClick={stopGeneration} className="stop-button">Stop</button>
                  ) : (
                    <button onClick={sendMessage} disabled={!input.trim()}>Send</button>
                  )}
                </div>
              </div>
              <p className="ai-caveat">AI can make mistakes. Please verify important facts manually and independently validate any output before relying on it.</p>
            </footer>
          </>
        ) : (
          <section className="settings-workspace">
            <div className="settings-tabs" role="tablist" aria-label="Settings Tabs">
              {(['defaults', 'system-prompt', 'ollama-models'] as SettingsTab[]).map((tab) => (
                <div key={tab} role="tab" tabIndex={0} aria-selected={settingsTab === tab} className={`settings-tab ${settingsTab === tab ? 'active' : ''}`} onClick={() => setSettingsTab(tab)} onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && setSettingsTab(tab)}>
                  {tab === 'defaults' ? 'Defaults' : tab === 'system-prompt' ? 'System Prompt' : 'Ollama Models'}
                </div>
              ))}
            </div>

            <div className="settings-page-grid">
              {settingsTab === 'defaults' && (
                <div id="settings-panel-defaults" role="tabpanel" className="settings-card">
                  <h3>Defaults</h3>
                  <div className="control-group compact">
                    <label htmlFor="defaultProvider">Default Provider</label>
                    <select id="defaultProvider" value={settings.defaultProvider} onChange={(e) => setSettings((prev) => ({ ...prev, defaultProvider: e.target.value }))}>
                      {[...new Set(availableModels.map((m) => m.provider))].map((providerName) => (
                        <option key={providerName} value={providerName}>{providerName}</option>
                      ))}
                    </select>
                  </div>
                  <div className="control-group compact">
                    <label htmlFor="defaultModel">Default Model</label>
                    <select id="defaultModel" value={settings.defaultModel} onChange={(e) => setSettings((prev) => ({ ...prev, defaultModel: e.target.value }))}>
                      {availableModels.filter((m) => m.provider === settings.defaultProvider).map((m) => (
                        <option key={`${m.provider}-${m.name}`} value={m.name}>{m.name}</option>
                      ))}
                    </select>
                  </div>
                  <div className="control-group compact checkbox-row">
                    <label htmlFor="enableBackgrounds">Enable backgrounds</label>
                    <input id="enableBackgrounds" type="checkbox" checked={settings.enableBackgrounds} onChange={(e) => setSettings((prev) => ({ ...prev, enableBackgrounds: e.target.checked }))} />
                  </div>
                </div>
              )}

              {settingsTab === 'system-prompt' && (
                <div id="settings-panel-system-prompt" role="tabpanel" className="settings-card">
                  <h3>System Prompt</h3>
                  <div className="prompt-manager-layout">
                    <div className="prompt-list-pane">
                      <label>Saved Prompts</label>
                      <div className="prompt-table-wrap">
                        <table className="prompt-table">
                          <thead><tr><th>Name</th><th>Creator</th></tr></thead>
                          <tbody>
                            {pagedPromptPresets.map((preset) => (
                              <tr key={preset.id} className={preset.id === selectedPromptPresetId ? 'active' : ''} onClick={() => setSelectedPromptPresetId(preset.id)}>
                                <td>{preset.name}</td>
                                <td>{preset.creatorName}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <div className="prompt-pagination">
                        <button disabled={promptPage <= 1} onClick={() => setPromptPage((prev) => Math.max(1, prev - 1))}>Prev</button>
                        <span>{promptPage}/{promptPageCount}</span>
                        <button disabled={promptPage >= promptPageCount} onClick={() => setPromptPage((prev) => Math.min(promptPageCount, prev + 1))}>Next</button>
                      </div>
                      <div className="project-create-row">
                        <input id="newPromptName" value={newPromptName} onChange={(e) => setNewPromptName(e.target.value)} placeholder="New preset name" />
                        <button onClick={createPromptPreset}>Add</button>
                      </div>
                    </div>
                    <div className="prompt-editor-pane">
                      <div className="control-group compact">
                        <label htmlFor="promptPresetTitle">Title</label>
                        <input id="promptPresetTitle" value={editingPresetName} onChange={(e) => setEditingPresetName(e.target.value)} maxLength={60} placeholder="Preset name" />
                      </div>
                      <div className="control-group compact">
                        <label htmlFor="systemPrompt">Prompt Body</label>
                        <textarea id="systemPrompt" className="settings-textarea" value={settings.systemPrompt} onChange={(e) => setSettings((prev) => ({ ...prev, systemPrompt: e.target.value }))} rows={10} placeholder="Prompt instructions for selected preset" />
                      </div>
                      <div className="project-create-row">
                        <button onClick={saveCurrentPromptPreset}>Save Preset</button>
                        <button onClick={deleteSelectedPromptPreset}>Delete Preset</button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {settingsTab === 'ollama-models' && (
                <div id="settings-panel-ollama-models" role="tabpanel" className="settings-card">
                  <h3>Ollama Models</h3>
                  <div className="control-group compact">
                    <label>Available Models</label>
                    <ul className="prompt-table-wrap" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                      {availableModels.filter((m) => m.provider === 'ollama').map((m) => (
                        <li key={m.name} style={{ padding: '6px 4px' }}>{m.name}</li>
                      ))}
                      {availableModels.filter((m) => m.provider === 'ollama').length === 0 && (
                        <li style={{ padding: '6px 4px' }} className="settings-hint">No Ollama models available.</li>
                      )}
                    </ul>
                  </div>
                  {isGlobalAdmin && (
                    <div className="control-group compact">
                      <label htmlFor="ollamaPull">Download Model (Global Admin only)</label>
                      <div className="project-create-row">
                        <input id="ollamaPull" value={ollamaPullModel} onChange={(e) => setOllamaPullModel(e.target.value)} placeholder="e.g. llama3.2:3b" />
                        <button onClick={downloadOllamaModel}>Pull</button>
                      </div>
                      {ollamaPullStatus && <span className="settings-hint">{ollamaPullStatus}</span>}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="settings-page-actions">
              <button className="settings-save" onClick={saveSettings}>Save Settings</button>
              {settingsStatus && <span className="settings-hint">{settingsStatus}</span>}
            </div>
          </section>
        )}
      </main>

      <aside className={`doc-sidebar${docSidebarOpen ? ' open' : ''}`} aria-label="Ingested documents">
        <div className="doc-sidebar-header">
          <h3>Documents</h3>
          <button className="doc-sidebar-close" onClick={() => setDocSidebarOpen(false)} aria-label="Close document panel">×</button>
        </div>
        {docSidebar.length === 0 ? (
          <p className="doc-sidebar-empty">Drop a PDF, DOCX, or TXT into the chat to index it.</p>
        ) : (
          <ul className="doc-sidebar-list">
            {docSidebar.map((entry) => {
              const sessionTitle = sessions.find((s) => s.id === entry.sessionId)?.title ?? 'Unknown session'
              return (
                <li key={entry.id} className={`doc-entry status-${entry.status}`}>
                  <div className="doc-entry-name" title={entry.name}>{entry.name}</div>
                  <div className="doc-entry-meta">{sessionTitle}</div>
                  {entry.status === 'uploading' && (
                    <div className="doc-entry-phase"><span className="doc-phase-spinner" /><span className="doc-phase-label">Uploading…</span></div>
                  )}
                  {entry.status === 'ingesting' && (
                    <div className="doc-entry-phase">
                      <div className="doc-progress-track"><div className="doc-progress-bar" style={{ width: `${entry.progress ?? 0}%` }} /></div>
                      <span className="doc-phase-label">{entry.progress ?? 0}%</span>
                    </div>
                  )}
                  {entry.status === 'done' && (
                    <div className="doc-entry-phase">
                      <div className="doc-progress-track"><div className="doc-progress-bar" style={{ width: '100%', background: '#22c55e' }} /></div>
                      <span className="doc-phase-label done">✓ {entry.pages ? `${entry.pages} pages` : `${entry.chunks ?? 0} chunks`} indexed</span>
                    </div>
                  )}
                  {entry.status === 'error' && (
                    <div className="doc-entry-phase"><span className="doc-phase-label error">✗ {entry.error ?? 'Upload failed'}</span></div>
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </aside>

      {lightbox && createPortal(
        <div className="lightbox-backdrop" onClick={() => setLightbox(null)} role="dialog" aria-modal="true" aria-label={lightbox.name}>
          <div className="lightbox-box" onClick={(e) => e.stopPropagation()}>
            <div className="lightbox-header">
              <span className="lightbox-title">{lightbox.name}</span>
              <button className="lightbox-close" onClick={() => setLightbox(null)} aria-label="Close">×</button>
            </div>
            <div className="lightbox-body">
              {lightbox.type === 'image' ? (
                <img src={lightbox.dataUrl} alt={lightbox.name} className="lightbox-img" />
              ) : (
                <iframe src={lightbox.dataUrl} title={lightbox.name} className="lightbox-pdf" />
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}

export default ClinicalAIAssistant
